import { isEqual } from "lodash";

import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import MedicationTable, { MedicationStatus, MedicationTableItem } from "@/Flowsheets/Components/MedicationTable/MedicationTable";
import { MedicationDirectivesHelper } from "@/Flowsheets/Helpers/MedicationDirectivesHelper";
import { ActiveMedicationCareElement } from "@/Flowsheets/Models/CareElements/ActiveMedicationCareElement";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { EnumPatientSummaryMedicationDisplayStatus } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationDisplayStatus";
import { MedicationLegalStatusHelper } from "@/Patient/Models/Medication/Enums/MedicationLegalStatus";
import { ICareComponentProps } from "./Types";

interface ActiveMedicationCareComponent extends CareComponent {
    CareElements: ActiveMedicationCareElement[];
}

/**
 * MedicationCareComponent handles loading and displaying medication
 */
const MedicationCareComponent = React.memo(
    ({ state }: Readonly<ICareComponentProps<ActiveMedicationCareComponent>>): JSX.Element => {
        const mapToMedicationTableItems = (elements: ActiveMedicationCareElement[]): MedicationTableItem[] => elements.map(element => {
            const elementWithDosageUnits = {
                ...element,
                Dosages: element.Dosages.map(dosage => ({
                    ...dosage,
                    DosageUnitValue: MedicationDirectivesHelper.getDosageUnitValue(dosage)
                })),
                IsNarcotic: MedicationLegalStatusHelper.isNarcotic(element.LegalStatus)
            };

            let medicationStatus: MedicationStatus;
            if (element.PrescriptionMedicationStatus === PrescriptionMedicationStatus.Suspended) {
                medicationStatus = MedicationStatus.Suspended;
            }
            else if (element.SummaryDisplayStatus === EnumPatientSummaryMedicationDisplayStatus.ShowActive) {
                medicationStatus = MedicationStatus.Active;
            }
            else {
                medicationStatus = MedicationStatus.Expired;
            }

            return {
                id: element.Id,
                name: MedicationDirectivesHelper.getName(element),
                status: medicationStatus,
                startDate: element.RefillStartDate ? DateHelper.formatIsoDateOnly(element.RefillStartDate) : "",
                endDate: DateHelper.formatIsoDateOnly(MedicationDirectivesHelper.getEffectiveEndDate(element)),
                dosage: MedicationDirectivesHelper.getDosageString(elementWithDosageUnits, { includeNarcoticFreq: false }),
                quantity: MedicationDirectivesHelper.getMedicationQuantities({
                    dosages: element.Dosages,
                    refillQuantity: element.RefillQuantity,
                    initialRefillQuantity: element.InitialRefillQuantity
                }).filter(p => !IsNullOrUndefined(p)).join(", "),
                renewals: MedicationDirectivesHelper.getRenewalString(elementWithDosageUnits),
                duration: MedicationDirectivesHelper.getDurationInDaysString(element.TotalDurationManualDays)
            };
        });

        const medicationTableItems = mapToMedicationTableItems(state.CareElements);

        return (
            <MedicationTable
                medications={medicationTableItems}
            />
        );
    },
    (prev, next) => isEqual(prev.state, next.state) && prev.isEditable === next.isEditable
);

export { MedicationCareComponent };
